import React, { useState, useEffect } from "react";

import { observer } from "mobx-react-lite";
import ManageBlockModal from "./ManageBlockModal";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  IconButton,
  CircularProgress,
  Select,
  MenuItem,
} from "@mui/material";

import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import channelStore from "../../Store/ChannelStore";
import { toast } from "react-toastify";
import { getErrorMessage } from "../../utils/errorHandler";

const ManageBlock = observer(
  ({
    blockStore,
    responseFilmList,
    getDataFilmList,
    repeatLastFilmListRequest,
    getDataListChannels,
  }) => {
    const [newBlockName, setNewBlockName] = useState("");
    const [newBlockBundleId, setNewBlockBundleId] = useState(0);
    const [isAdding, setIsAdding] = useState(false);
    const [openModal, setOpenModal] = useState(false);
    const [selectedBlock, setSelectedBlock] = useState(null);

    const [filterBundleId, setFilterBundleId] = useState(0);

    useEffect(() => {
      blockStore.getBlockList();
      channelStore.getChannels();
    }, [blockStore]);

    const availableChannels = Array.isArray(channelStore.channels)
      ? channelStore.channels
      : [];

    const handleAddBlock = async () => {
      if (!newBlockName.trim()) return;

      setIsAdding(true);
      try {
        await blockStore.addBlock(
          newBlockName.trim(),
          Number(newBlockBundleId)
        );
        setNewBlockName("");
        setNewBlockBundleId(0);
      } catch (error) {
        toast.error(getErrorMessage(error, "Не удалось создать блок"));
      } finally {
        setIsAdding(false);
      }
    };

    const handleDeleteBlock = async (blockId) => {
      if (!window.confirm("Вы действительно хотите удалить этот блок?")) return;

      try {
        await blockStore.deleteBlock(blockId);
      } catch (error) {
        toast.error(getErrorMessage(error, "Не удалось удалить блок"));
      }
    };

    const handleOpenBlockModal = (block) => {
      setSelectedBlock(block);
      setOpenModal(true);
    };

    const handleCloseModal = () => {
      setOpenModal(false);
      setSelectedBlock(null);
    };

    const handleBundleChange = async (block_id, newBundleId) => {
      try {
        await blockStore.editBlockBundle(block_id, Number(newBundleId));
      } catch (error) {
        toast.error(getErrorMessage(error, "Не удалось изменить канал блока"));
      }
    };

    // Фильтрация блоков
    const filteredBlocks = blockStore.responseBlockList?.data?.filter(
      (block) => {
        if (filterBundleId === 0) return true;
        return Number(block.bundle_id || 0) === Number(filterBundleId);
      }
    );

    return (
      <Box
        sx={{
          p: 2,
          flex: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Typography variant="h5" gutterBottom align="center">
          Управление блоками
        </Typography>

        {/* Форма добавления нового блока и фильтр */}
        <Box
          sx={{
            mb: 3,
            display: "flex",
            gap: 2,
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <TextField
              label="Название нового блока"
              variant="outlined"
              size="small"
              value={newBlockName}
              onChange={(e) => setNewBlockName(e.target.value)}
              sx={{ width: 300 }}
              onKeyPress={(e) => e.key === "Enter" && handleAddBlock()}
            />

            <Select
              size="small"
              value={newBlockBundleId}
              onChange={(e) => setNewBlockBundleId(Number(e.target.value))}
              sx={{ width: 220 }}
              displayEmpty
            >
              <MenuItem value={0}>Выберите канал</MenuItem>
              {availableChannels.map((channel) => (
                <MenuItem key={channel.id} value={Number(channel.id)}>
                  {channel.name}
                </MenuItem>
              ))}
            </Select>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddBlock}
              disabled={isAdding || !newBlockName.trim()}
            >
              {isAdding ? <CircularProgress size={20} /> : "Добавить блок"}
            </Button>
          </Box>

          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <Typography variant="body2" fontWeight="bold">
              Фильтровать по каналу:
            </Typography>
            <Select
              size="small"
              value={filterBundleId}
              onChange={(e) => setFilterBundleId(Number(e.target.value))}
              sx={{ width: 220 }}
              displayEmpty
            >
              <MenuItem value={0}>Все каналы</MenuItem>
              {availableChannels.map((channel) => (
                <MenuItem key={channel.id} value={Number(channel.id)}>
                  {channel.name}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>

        {/* Таблица блоков */}
        <TableContainer
          component={Paper}
          sx={{ flex: 1, maxHeight: "calc(100vh - 220px)" }}
        >
          <Table stickyHeader>
            <TableHead>
              {/* Строка с заголовками столбцов */}
              <TableRow
                sx={{
                  height: "20px",
                  "& td, & th": { padding: "4px" },
                }}
              >
                <TableCell width="80px">
                  <strong>Block ID</strong>
                </TableCell>
                <TableCell>
                  <strong>Название блока</strong>
                </TableCell>
                <TableCell width="80px" align="center">
                  <strong>Статус</strong>
                </TableCell>
                <TableCell
                  sx={{
                    textAlign: "right",
                  }}
                >
                  <strong>Фильмов</strong>
                </TableCell>
                <TableCell align="center">
                  <strong>Канал</strong>
                </TableCell>
                <TableCell align="center" width="100px">
                  <strong>Действия</strong>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {blockStore.loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : filteredBlocks?.length > 0 ? (
                filteredBlocks.map((block) => {
                  const isReady = block.count >= 3 && block.count <= 5;
                  return (
                    <TableRow
                      key={block.block_id}
                      hover
                      sx={{
                        height: "20px",
                        "& td, & th": { padding: "4px" },
                        backgroundColor: isReady ? "#f0f9f0" : "inherit",
                      }}
                    >
                      <TableCell>{block.block_id}</TableCell>
                      <TableCell>
                        <Typography
                          component="span"
                          onClick={() => handleOpenBlockModal(block)}
                          sx={{
                            cursor: "pointer",
                            color: "#1976d2",
                            "&:hover": { textDecoration: "underline" },
                          }}
                        >
                          {block.name}
                        </Typography>
                      </TableCell>
                      {/* Новый столбец "Статус" */}
                      <TableCell align="center">
                        {isReady ? (
                          <CheckCircleIcon
                            sx={{ color: "#28a745", fontSize: 20 }}
                            titleAccess="Готов"
                          />
                        ) : (
                          <WarningIcon
                            sx={{ color: "#ffc107", fontSize: 20 }}
                            titleAccess="Не готов"
                          />
                        )}
                      </TableCell>
                      {/* Столбец "Фильмов" */}
                      <TableCell align="right">
                        <strong
                          style={{ color: isReady ? "#28a745" : "#d32f2f" }}
                        >
                          {block.count || 0}
                        </strong>
                      </TableCell>

                      <TableCell>
                        <Select
                          size="small"
                          fullWidth
                          value={Number(block.bundle_id || 0)}
                          onChange={(e) =>
                            handleBundleChange(block.block_id, e.target.value)
                          }
                        >
                          <MenuItem value={0}>Не привязан</MenuItem>
                          {availableChannels.map((channel) => (
                            <MenuItem
                              key={channel.id}
                              value={Number(channel.id)}
                            >
                              {channel.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </TableCell>

                      <TableCell align="center">
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => handleDeleteBlock(block.block_id)}
                          title="Удалить блок"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                    <Typography variant="body1" color="text.secondary">
                      {filterBundleId !== 0
                        ? "Нет блоков для выбранного канала"
                        : "Блоки пока не созданы. Добавьте первый блок выше."}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <ManageBlockModal
          open={openModal}
          handleClose={handleCloseModal}
          block={selectedBlock}
          blockStore={blockStore}
        />
      </Box>
    );
  }
);

export default ManageBlock;
